import axios from "axios";
import { config } from "../config/env";
import { getTenantSlugFromHostname } from "../utils/tenant";
import { auth } from "../config/firebase";

export const api = axios.create({ baseURL: config.apiUrl, timeout: config.requestTimeoutMs });

api.interceptors.request.use(async (request) => {
  const token = await auth.currentUser?.getIdToken();
  if (token) request.headers.Authorization = `Bearer ${token}`;
  const tenant = getTenantSlugFromHostname();
  if (tenant) request.headers["X-School-Slug"] = tenant;
  return request;
});

api.interceptors.response.use((response) => response, async (error) => {
  const original = error.config;
  if (error.response?.status === 401 && auth.currentUser && !original?._retried) {
    original._retried = true;
    try {
      const token = await auth.currentUser.getIdToken(true);
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch (_) {
      window.dispatchEvent(new Event("edunexa:session-expired"));
    }
  }
  if (error.response?.status === 401 && auth.currentUser) {
    window.dispatchEvent(new Event("edunexa:session-expired"));
  }
  const detail = error.response?.data?.detail;
  const message = Array.isArray(detail) ? detail.map(item => `${item.loc?.slice(-1)[0] || "Field"}: ${item.msg || "Invalid value"}`).join(". ") : typeof detail === "string" ? detail : (error.response ? "Request failed. Please try again." : "Unable to connect to server. Please try again.");
  const normalized = new Error(message);
  normalized.status = error.response?.status;
  return Promise.reject(normalized);
});

export const authApi = {
  async me() {
    const { data } = await api.get("/api/v1/auth/me");
    return data;
  },
  async profile() { return (await api.get("/api/v1/auth/profile")).data; },
  async updateProfile(payload) { return (await api.put("/api/v1/auth/profile", payload)).data; },
  async uploadProfilePhoto(file) { const form = new FormData(); form.append("file", file); return (await api.post("/api/v1/auth/profile/photo", form)).data; },
};

export const leaveApi = {
  async list() { return (await api.get("/api/v1/leaves")).data; },
  async create(payload) { return (await api.post("/api/v1/leaves", payload)).data; },
  async review(id, status, review_note = null) { return (await api.patch(`/api/v1/leaves/${id}`, { status, review_note })).data; },
};

export const attendanceApi = {
  async list({ attendanceDate, className, section }) {
    return (await api.get("/api/v1/attendance", { params: { attendance_date: attendanceDate, class_name: className, section } })).data;
  },
  async save(attendanceDate, records) {
    return (await api.put("/api/v1/attendance", { attendance_date: attendanceDate, records })).data;
  },
  async studentHistory(studentId, month) { return (await api.get(`/api/v1/students/${studentId}/attendance-history`, { params: { month } })).data; },
  async myStudents(month) { return (await api.get("/api/v1/attendance/my-students", { params: { month } })).data; },
};

export const faceAttendanceApi = {
  async dashboard() { return (await api.get("/api/v1/face-attendance/me")).data; },
  async verify(file, action) {
    const form = new FormData();
    form.append("selfie", file, "attendance-selfie.jpg");
    form.append("action", action);
    return (await api.post("/api/v1/face-attendance/verify", form)).data;
  },
  async mark(action, verificationToken) {
    return (await api.post(`/api/v1/face-attendance/${action}`, { verification_token: verificationToken })).data;
  },
};

export const schoolApi = {
  async workspace(schoolSlug) { return (await api.get("/api/v1/school/workspace", { headers: { "X-School-Slug": schoolSlug } })).data; },
  async students(schoolSlug) { return (await api.get("/api/v1/students", { headers: { "X-School-Slug": schoolSlug } })).data; },
  async createStudent(schoolSlug, payload) { return (await api.post("/api/v1/students", payload, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async updateStudent(schoolSlug, id, payload) { return (await api.put(`/api/v1/students/${id}`, payload, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteStudent(schoolSlug, id) { return api.delete(`/api/v1/students/${id}`, { headers: { "X-School-Slug": schoolSlug } }); },
  async bulkUploadStudents(schoolSlug, file) { const form = new FormData(); form.append("file", file); return (await api.post("/api/v1/students/bulk-upload", form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async uploadStudentPhoto(schoolSlug, id, file) { const form = new FormData(); form.append("file", file); return (await api.post(`/api/v1/students/${id}/photo`, form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteStudentPhoto(schoolSlug, id) { return (await api.delete(`/api/v1/students/${id}/photo`, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async uploadStudentDocument(schoolSlug, id, file, label) { const form = new FormData(); form.append("file", file); form.append("label", label); return (await api.post(`/api/v1/students/${id}/documents`, form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteStudentDocument(schoolSlug, id, documentId) { return (await api.delete(`/api/v1/students/${id}/documents/${documentId}`, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async employees(schoolSlug, role) { return (await api.get(`/api/v1/employees/${role}`, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async createEmployee(schoolSlug, role, payload) { return (await api.post(`/api/v1/employees/${role}`, payload, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async updateEmployee(schoolSlug, role, id, payload) { return (await api.put(`/api/v1/employees/${role}/${id}`, payload, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteEmployee(schoolSlug, role, id) { return api.delete(`/api/v1/employees/${role}/${id}`, { headers: { "X-School-Slug": schoolSlug } }); },
  async bulkUploadEmployees(schoolSlug, role, file) { const form = new FormData(); form.append("file", file); return (await api.post(`/api/v1/employees/${role}/bulk-upload`, form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async uploadEmployeePhoto(schoolSlug, role, id, file) { const form = new FormData(); form.append("file", file); return (await api.post(`/api/v1/employees/${role}/${id}/photo`, form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteEmployeePhoto(schoolSlug, role, id) { return (await api.delete(`/api/v1/employees/${role}/${id}/photo`, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async uploadEmployeeDocument(schoolSlug, role, id, file, label) { const form = new FormData(); form.append("file", file); form.append("label", label); return (await api.post(`/api/v1/employees/${role}/${id}/documents`, form, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async deleteEmployeeDocument(schoolSlug, role, id, documentId) { return (await api.delete(`/api/v1/employees/${role}/${id}/documents/${documentId}`, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async registerEmployeeFaceFromDocument(schoolSlug, role, id, documentId) { return (await api.post(`/api/v1/employees/${role}/${id}/face-from-document/${documentId}`, null, { headers: { "X-School-Slug": schoolSlug } })).data; },
  async users(schoolSlug) { return (await api.get("/api/v1/school/users", { headers: { "X-School-Slug": schoolSlug } })).data; },
  async createUser(schoolSlug, payload) { return (await api.post("/api/v1/school/users", payload, { headers: { "X-School-Slug": schoolSlug } })).data; },
};

export const subscriptionApi = {
  async plan() { return (await api.get("/api/v1/subscriptions/plan")).data; },
  async status() { return (await api.get("/api/v1/subscriptions/status")).data; },
  async onboard(payload) { return (await api.post("/api/v1/subscriptions/onboarding", payload)).data; },
  async checkout(couponCode = "", months = 1) { return (await api.post("/api/v1/subscriptions/checkout", { code: couponCode, months })).data; },
  async verifyPayment(payload) { return (await api.post("/api/v1/subscriptions/payments/verify", payload)).data; },
  async redeem(code) { return (await api.post("/api/v1/subscriptions/coupons/redeem", { code })).data; },
  async createCoupon(payload) { return (await api.post("/api/v1/subscriptions/coupons", payload)).data; },
  async manualGrant(payload) { return (await api.post("/api/v1/subscriptions/manual-grants", payload)).data; },
  async sendRenewalNotification(schoolId) { return (await api.post(`/api/v1/subscriptions/admin/schools/${schoolId}/renewal-notification`)).data; },
  async deleteSchool(schoolId) { return (await api.delete(`/api/v1/subscriptions/admin/schools/${schoolId}`)).data; },
  async adminDashboard() { return (await api.get("/api/v1/subscriptions/admin/dashboard")).data; },
  async updatePlan(payload) { return (await api.put("/api/v1/subscriptions/admin/plan", payload)).data; },
  async addExpense(payload) { return (await api.post("/api/v1/subscriptions/admin/expenses", payload)).data; },
};
